import { v4 as uuid } from 'uuid';
import Cookie from 'js-cookie';
import querystring from 'query-string';

const URL_PARTS = ['hash', 'host', 'hostname', 'href', 'pathname', 'port', 'protocol', 'search'];

/**
 * SessionKit options.
 * @typedef {Object} SessionKitOptions
 * @property {string} [cookieName='sk-session-id'] - The name of the cookie to use.
 * @property {string} [cookieDomain=''] - The domain of the cookie to use.
 * @property {string} [cookiePath='/'] - The path of the cookie to use.
 * @property {number} [cookieExpires=365] - The number of days to keep the cookie.
 * @property {boolean} [cookieSecure=true] - Whether the cookie should be secure.
 * @property {boolean} [cookieSameSite='None'] - Whether the cookie should be sameSite.
 */

class SessionKit {
  /**
   * @param {SessionKitOptions} options
   * @returns {string} The session id.
   */
  constructor(options) {
    this.options = Object.assign(
      {
        cookieName: 'sk-session-id',
        cookieDomain: '.' + this.constructor.baseDomain(window.location.href),
        cookiePath: '/',
        cookieExpires: 365,
        cookieSecure: true,
        cookieSameSite: 'None',
      },
      options
    );

    this.sessionId = this.getSessionId();
  }

  static get version() {
    return '1.3.1';
  }

  /**
   * Get the session id from the cookie or create a new one.
   * @params {boolean} [reset=false] - Whether to force a new session id.
   * @returns {string} The session id.
   */
  getSessionId(reset = false) {
    const { cookieName, cookieDomain, cookiePath, cookieExpires, cookieSecure, cookieSameSite } =
      this.options;
    const queryParams = this.constructor.queryParams(window.location.href);
    let sessionIdParams = queryParams[cookieName];
    let sessionIdCookie = Cookie.get(cookieName);
    let sessionIdStorage = window.localStorage ? localStorage.getItem(cookieName) : null;

    if (sessionIdParams) {
      reset = true;
      sessionIdCookie = sessionIdParams;
    }

    if (!sessionIdCookie || reset) {
      if (reset) {
        sessionIdCookie = sessionIdParams || this.constructor.createSessionId();
      } else {
        sessionIdCookie = sessionIdStorage || this.constructor.createSessionId();
      }

      Cookie.set(cookieName, sessionIdCookie, {
        expires: cookieExpires,
        domain: cookieDomain,
        path: cookiePath,
        secure: cookieSecure,
        sameSite: cookieSameSite,
      });
    }

    if (!sessionIdStorage || reset) {
      sessionIdStorage = sessionIdCookie;
      if (window.localStorage) {
        localStorage.setItem(cookieName, sessionIdStorage);
      }
    }

    return sessionIdCookie || sessionIdStorage;
  }

  /**
   * Generate a UUID.
   * @returns {string} The session id.
   */
  static createSessionId() {
    return uuid();
  }

  static get cookies() {
    return Cookie;
  }

  static get querystring() {
    return querystring;
  }

  /**
   * Return an object with a subset of the properties of the given object
   * selected by the given keys.
   * @param {*} obj The original object.
   * @param {string[]} keys The keys to select.
   * @returns {Object} The object with the selected keys.
   */
  static pick(obj, ...keys) {
    return keys.reduce((acc, key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});
  }

  /**
   * Return an object with a subset of the properties of the given object
   * omitted by the given keys.
   * @param {*} obj The original object.
   * @param {string[]} keys The keys to omit.
   * @returns {Object} The object with the selected keys removed.
   */
  static omit(obj, ...keys) {
    return Object.keys(obj).reduce((acc, key) => {
      if (!keys.includes(key)) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});
  }

  /**
   * Given a url or a query string, return an object with the query parameters.
   * @param {string} url
   * @param {Object} options
   * @returns {Object} The query params
   */
  static queryParams(url, options = {}) {
    const urlParts = this.urlParts(url);
    const query = urlParts.search.slice(1);
    return this.querystring.parse(query, options);
  }

  /**
   * Extract and return the parts of a url.
   * @param {string} url
   * @returns {Object} The url parts.
   */
  static urlParts(url) {
    const a = document.createElement('a');
    a.href = url;
    return URL_PARTS.reduce((acc, key) => {
      acc[key] = a[key];
      return acc;
    }, {});
  }

  /**
   * Get the domain of a url.
   * @param {string} url
   * @returns {string} The domain.
   */
  static domain(url) {
    const { hostname } = this.urlParts(url);
    return hostname;
  }

  /**
   * Get the base domain of a url.
   * @param {string} url
   * @returns {string} The base domain.
   */
  static baseDomain(url) {
    const { hostname } = this.urlParts(url);
    if (/^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/.test(hostname)) {
      return hostname;
    }
    const parts = hostname.split('.');
    return parts.length > 2 ? parts.slice(-2).join('.') : hostname;
  }

  /**
   * Create a promise paired with a cancellation function that can be used to
   * cancel the promise.
   * @param {string} message The cancellation message
   * @returns {Promise<Error>} A promise that resolves when the user cancels.
   */
  static createCancellableSignal(message = 'Cancelled') {
    const ret = {};
    ret.signal = new Promise((resolve, reject) => {
      ret.cancel = () => {
        reject(new Error(message));
      };
    });

    return ret;
  }

  /**
   * Accepts a function and returns a promise that resolves when the function
   * returns a truthy value. The function can be cancelled by calling the
   * cancellation function of a given signal promise.
   * @param {function} fn The function to execute.
   * @param {integer} interval The interval to check the function.
   * @param {integer} timeout The number of milliseconds to wait before timing out.
   * @param {Promise} signal The signal promise used to cancel the function.
   * @returns {Promise} A promise that resolves when the function returns a truthy value.
   */
  static waitFor(fn, interval = 100, timeout = 10000, signal) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const intervalId = setInterval(() => {
        const result = fn();
        if (result) {
          clearInterval(intervalId);
          resolve(result);
        } else if (Date.now() - start > timeout) {
          clearInterval(intervalId);
          reject(new Error('Timed out'));
        }
      }, interval);
      if (signal) {
        signal.catch((err) => {
          reject(err);
          clearInterval(intervalId);
        });
      }
    });
  }
}

export default SessionKit;
